import { useAuth } from '@app/Contexts/AuthProvider';
import { useProfile } from '@app/Contexts/ProfileProvider';
import { t } from 'i18next';
import { Col, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { StatusSourceFormDelegator } from './Components/Forms/StatusSource/StatusSourceFormDelegator';
import { PersonalDetailsFormDelegator } from './Components/Forms/PersonalDetails';

/**
 * ProfileIndex
 *
 * Top-level page component for displaying and editing an agent profile.
 *
 * Architectural role:
 * - Owns page-level concerns (layout, title, context wiring)
 * - Retrieves domain entities from context providers
 * - Passes explicit identifiers and initial data into feature delegators
 *
 * This component:
 * - Does NOT contain form logic
 * - Does NOT perform API calls
 * - Does NOT manage modal workflows
 *
 * All business workflows are delegated to feature-level components.
 */
export const ProfileIndex = () => {
  /**
   * Active profile being viewed or edited.
   *
   * Provided by the ProfileProvider and treated as the
   * authoritative source of profile domain data.
   */
  const { profile } = useProfile();

  /**
   * Auth/session context for the currently logged-in user.
   *
   * Used here only to determine the impacted user ID for downstream workflows.
   */
  const session = useAuth();

  return (
    <>
      {/* Page-level metadata */}
      <Helmet>
        <title>
          {profile.getIndName()}: {t('agent.profile.pageTitle')}
        </title>
      </Helmet>

      <Row className="mt-1">
        <Col className="mb-3">
          {/* 
            Status / Source form feature.

            Responsibilities delegated here:
            - Fetching and managing source options
            - Handling create-source modal workflows
            - Submitting updates to the profile
            - Managing saving/loading state

            This page supplies:
            - Explicit impacted user ID
            - Explicit profile ID
            - Raw domain values used to initialise the form
          */}
          <StatusSourceFormDelegator
            userId={session.getUser().getUserId()}
            profileId={profile.getIndId()}
            initialValues={{
              indStatus: profile.getIndStatus(),
              indSource: profile.getIndSource(),
              indReferral: profile.getIndReferral(),
              indReferralTo: profile.getIndReferralTo(),
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mb-3">
          <PersonalDetailsFormDelegator
            userId={session.getUser().getUserId()}
            profileId={profile.getIndId()}
            initialValues={{
              indGender: profile.getIndGender(),
              indPrefix: profile.getIndPrefix(),
              indFirstname: profile.getIndFirstname(),
              indMiddlename: profile.getIndMiddlename(),
              indLastname: profile.getIndLastname(),
              indSuffix: profile.getIndSuffix(),
              indNickname: profile.getIndNickname(),
              indDob: profile.getIndDob(),
              indFacts: profile.getIndFacts(),
              indSpouse: undefined,
              indMaritalStatus: profile.getIndMaritalStatus(),
              indDesignation: profile.getIndDesignation(),
              indOccupation: profile.getIndOccupation(),
              indResident: profile.getIndResident(),
              prefLang: undefined,
            }}
          />
        </Col>
      </Row>
    </>
  );
};
